import type { ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { Link } from "react-router";
import { isApiError, toApiError } from "@/shared/api/errors";
import { describeError } from "@/shared/api/playerError";
import { Icon, type IconName } from "./Icon";
import { EmptyArtImage, type EmptyArt } from "./emptyArt";
import { Button } from "./primitives";

/** 统一的 loading / empty / error / disabled / pending 呈现。模块不自造各自的错误页。 */
export function LoadingState({ label = "正在接收信号…", lines = 3 }: { label?: string; lines?: number }) {
  return (
    <div className="ps-stack" aria-busy="true" aria-live="polite">
      <span className="visually-hidden">{label}</span>
      {Array.from({ length: lines }, (_, index) => (
        <div key={index} className="ps-skeleton" style={{ height: index === 0 ? 120 : 64 }} />
      ))}
    </div>
  );
}

/**
 * pageHeading：整页只有这一个状态块（例如找不到的地址）时传，标题画成这一页的 h1（UI-BASE-01b，每页一个 h1）。
 * art：有空态插图时传（ASSET-UI-010，见 emptyArt.tsx），图替掉图标圆片；纯装饰，读屏不念（UI-EMPTY-ART-01）。
 */
export function EmptyState({ icon = "sparkle", art, title, children, action, pageHeading = false }: { icon?: IconName; art?: EmptyArt; title: string; children?: ReactNode; action?: ReactNode; pageHeading?: boolean }) {
  return (
    <div className="ps-state">
      {art ? (
        <EmptyArtImage art={art} />
      ) : (
        <div className="ps-state__icon">
          <Icon name={icon} />
        </div>
      )}
      {pageHeading ? <h1 className="ps-state__title">{title}</h1> : <div className="ps-state__title">{title}</div>}
      {children ? <div>{children}</div> : null}
      {action}
    </div>
  );
}

export function DisabledState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="ps-state ps-state--disabled">
      <div className="ps-state__icon">
        <Icon name="lock" />
      </div>
      <div className="ps-state__title">{title}</div>
      {children ? <div>{children}</div> : null}
      {action}
    </div>
  );
}

/**
 * 还没开放的功能（ERROR-RECOVERY-01，S-086「没开放的功能像出错」）：锁形图标、平静的一句，不是红色出错态、不抢读屏（没有 role=alert）。
 * 演示里没有的能力、后端还没装的能力都用它，不报成出了故障；页面可以在下面放一个去别处的入口（action）。
 */
export function NotOpenState({ title = "这里暂时还没开放", children, action }: { title?: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <DisabledState title={title} action={action}>
      {children ?? <p style={{ margin: 0 }}>准备好了会出现在这里。</p>}
    </DisabledState>
  );
}

/** 就近恢复的去处：回上一步、去别处（页面按自己的上级、兄弟页给；不给就只有“重试”或什么都不给）。 */
export interface ErrorExit {
  to: string;
  label: string;
}

function ErrorExits({ exits }: { exits?: ErrorExit[] }) {
  if (!exits?.length) return null;
  return (
    <div className="ps-row" style={{ justifyContent: "center" }}>
      {exits.map((exit) => (
        <Link key={`${exit.to}|${exit.label}`} className="ps-btn ps-btn--secondary" to={exit.to}>
          {exit.label}
        </Link>
      ))}
    </div>
  );
}

/**
 * 出错时的一块（文案见 shared/api/playerError 的 describeError）：一句标题、一句人话，再给就近的恢复——能再试的给“重试”，
 * 页面传了 exits 就给回上一步、去别处。还没开放、后端还没有这条路不当成出错（平静的说明，不是 role=alert）。
 * 用户 10-09 定稿（X-09／U-26「肯定不显示……后台查。」）：玩家端任何地方都不显示错误码、请求编号、原因码，
 * 也不做“展开看详情”的折叠区（原来这里收起的那一块已经拿掉）；要查只在运营后台查。
 * retrying：重新拉取进行中（QueryView 传 query.isFetching）——“重试”按钮显示在进行、不能连点，点了有回应（2026-09-29 美化）。
 */
export function ErrorState({ error, onRetry, retrying, exits }: { error: unknown; onRetry?: () => void; retrying?: boolean; exits?: ErrorExit[] }) {
  const err = toApiError(error);
  const view = describeError(err);
  if (view.kind === "unavailable") return <NotOpenState title={view.title} action={<ErrorExits exits={exits} />} />;
  if (view.kind === "noRoad") {
    return (
      <EmptyState icon="compass" title={view.title} action={<ErrorExits exits={exits} />}>
        {view.text}
      </EmptyState>
    );
  }
  if (view.kind === "login") {
    return (
      <div className="ps-state ps-state--error" role="alert">
        <div className="ps-state__icon">
          <Icon name="user" />
        </div>
        <div className="ps-state__title">{view.title}</div>
        <div>{view.text}</div>
        <Link className="ps-btn ps-btn--primary" to="/login">
          去登录
        </Link>
      </div>
    );
  }
  return (
    <div className="ps-state ps-state--error" role="alert">
      <div className="ps-state__icon">
        <Icon name="alert" />
      </div>
      <div className="ps-state__title">{view.title}</div>
      <div>{view.text}</div>
      {onRetry ? (
        <Button variant="primary" icon="refresh" loading={retrying} onClick={onRetry}>
          重试
        </Button>
      ) : null}
      <ErrorExits exits={exits} />
    </div>
  );
}

/**
 * 断线重连中（UI-RESILIENCE-01，2531 在候选计划重启时看到：服务只断了 3 秒，整屏就变成“信号暂时中断”）。
 * 旧画面照常在，顶上一条小提示，不挡点按（pointer-events: none）、不怪主人的网络；恢复后自动消失。读屏用 role="status" 报一次。
 */
export function ReconnectingNote() {
  return (
    <div className="ps-reconnecting ps-night" role="status" data-testid="reconnecting">
      <Icon name="refresh" size={14} />
      正在重新连接
    </div>
  );
}

/**
 * 查询结果的统一包装：loading → error → empty → data。
 * keepData（UI-RESILIENCE-01）：手上已有数据时，后台刷新失败（isError）或正在重试（failureCount>0）都照常显示旧数据，
 * 顶上叠 ReconnectingNote；还没有任何数据时照旧是整页的出错态。不传时与原来完全一样。
 * errorExits（ERROR-RECOVERY-01）：出错时给的回上一步、去别处，原样交给 ErrorState；不传时与原来一样。
 */
export function QueryView<T>({
  query,
  children,
  isEmpty,
  empty,
  loading,
  keepData = false,
  errorExits,
}: {
  query: UseQueryResult<T>;
  children: (data: T) => ReactNode;
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  loading?: ReactNode;
  keepData?: boolean;
  errorExits?: ErrorExit[];
}) {
  if (query.isPending) return <>{loading ?? <LoadingState />}</>;
  const kept = keepData && query.data !== undefined;
  if (query.isError && !kept) return <ErrorState error={query.error} onRetry={isApiError(query.error) && !query.error.retryable ? undefined : () => void query.refetch()} retrying={query.isFetching} exits={errorExits} />;
  const data = query.data as T;
  if (isEmpty?.(data)) return <>{empty ?? <EmptyState title="这里还空着" />}</>;
  const reconnecting = kept && (query.isError || query.failureCount > 0);
  return (
    <>
      {reconnecting ? <ReconnectingNote /> : null}
      {children(data)}
    </>
  );
}

export function PendingBadge({ label = "处理中" }: { label?: string }) {
  return (
    <span className="ps-chip ps-chip--sky" role="status">
      <Icon name="refresh" size={13} />
      {label}
    </span>
  );
}
